import { type Node, type XYPosition } from '@xyflow/react';
import { createContext, useContext } from 'react';

import { type DiagramSide } from '@/types/schema';

import {
  type DiagramEditorTool,
  type DiagramPlacementTool,
  type DiagramQuickAddTool,
} from '../components/DiagramEditor/diagramEditorTools';
import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import { type useReactFlowDiagram } from '../hooks/useReactFlowDiagram';
import {
  type DiagramBuilderEdge,
  type DiagramBuilderNode,
} from '../utils/diagram';
import { type DiagramConnectionEnds } from '../utils/diagramEdits';

/** The state of the diagram canvas. It changes when the user edits. */
export interface DiagramEditingState {
  /**
   * The saved document, with the pinned position of each tile, in the
   * GraphQL input shape. The edit actions change it, and the editor saves it.
   */
  diagram: DiagramBuilder_DiagramFragment;
  /**
   * True while the backend generates or saves the diagram. A locked canvas
   * accepts no edits and shows no edit controls.
   */
  locked: boolean;
  /**
   * The live React Flow diagram: the tiles, the connections, the drag state,
   * and the layout status. It comes from `diagram` and is not saved. Render
   * the canvas from it. Edit `diagram`, not the React Flow diagram.
   */
  reactFlowDiagram: ReturnType<typeof useReactFlowDiagram>;
  /** The current canvas tool. A locked canvas uses the select tool. */
  tool: DiagramEditorTool;
  /** The label that the user edits in place. Null when no label edit is open. */
  labelTarget: DiagramLabelTarget | null;
  /**
   * The node whose notes the user edits in the rich-text editor. The editor
   * shows its own toolbar in place of the selection toolbar. Null when no
   * notes editor is open.
   */
  notesEditorNodeID: string | null;
  canAddNode: boolean;
}

/**
 * The edit actions of the diagram canvas. Each action keeps its identity
 * between renders and reads the latest diagram. An edit does nothing while
 * the canvas is locked.
 */
export interface DiagramEditingActions {
  /** Replaces the diagram. */
  update: (diagram: DiagramBuilder_DiagramFragment) => void;
  setTool: (tool: DiagramEditorTool) => void;
  /**
   * True when the editor can add a connection between these nodes: both
   * nodes exist, they are different nodes, and the diagram has fewer than the
   * maximum number of connections.
   */
  canConnect: (sourceNodeID: string, targetNodeID: string) => boolean;
  /** Adds a connection between the sides of two nodes, if `canConnect` allows it. */
  connect: (ends: DiagramConnectionEnds) => void;
  /** Moves the ends of a connection to other nodes or sides. */
  reconnect: (edgeID: string, ends: DiagramConnectionEnds) => void;
  /**
   * Adds a tile or an icon and a connection to it from a side of
   * `source.nodeID`, as one edit. The side of the new node that faces the
   * source is at `position`, in canvas pixels. The new node goes in the
   * stage at its placed position.
   */
  addConnectedNode: (
    source: { nodeID: string; side: DiagramSide },
    position: XYPosition,
    tool: DiagramQuickAddTool
  ) => void;
  /**
   * Adds the element of a placement tool with its top-left corner at
   * `position`, in canvas pixels. A new node goes in the stage at
   * `position`. The line tool adds a stage divider at the height of
   * `position`. Then selects the select tool again.
   */
  placeElement: (tool: DiagramPlacementTool, position: XYPosition) => void;
  /**
   * Removes the saved tile positions and connection bends. Run the canvas
   * layout again after it, so that the tiles move to automatic positions.
   */
  resetPositions: () => void;
  /**
   * Saves the position of each tile in `tiles` after the user drags or
   * resizes `tile`. Also saves the width and height of `tile` when React
   * Flow sets them, after a resize. Each tile in `movedIDs` goes to the
   * stage at its vertical center.
   */
  placeNode: (
    tile: Node,
    tiles: Node[],
    movedIDs?: ReadonlySet<string>
  ) => void;
  openLabelEditor: (target: DiagramLabelTarget) => void;
  closeLabelEditor: () => void;
  openNotesEditor: (nodeID: string) => void;
  /** Clears `notesEditorNodeID` if it is `nodeID`. */
  closeNotesEditor: (nodeID: string) => void;
  editNode: (id: string, changes: Partial<DiagramBuilderNode>) => void;
  /**
   * Removes the nodes and connections with these IDs as one edit. Also
   * removes the connections of each removed node.
   */
  removeElements: (...ids: string[]) => void;
  editEdge: (id: string, changes: Partial<DiagramBuilderEdge>) => void;
  /** Sets the label of the divider before a stage. The tiles keep their stages. */
  editStageLabel: (stageID: string, label: string) => void;
  /**
   * Moves the divider before a stage to canvas height `y`. The tiles do not
   * move. The tiles that the divider passes change stage.
   */
  moveStageBoundary: (stageID: string, y: number) => void;
  /** Removes the divider before a stage. Its tiles go to the stage above it. */
  removeStageBoundary: (stageID: string) => void;
}

export type DiagramEditing = DiagramEditingState & DiagramEditingActions;

/**
 * A label that the user edits in place: the label of a connection, or the
 * label of the divider before a stage.
 */
export interface DiagramLabelTarget {
  kind: 'edge' | 'stage';
  id: string;
}

export const DiagramEditingContext = createContext<DiagramEditing | null>(null);

export function useDiagramEditing() {
  const context = useContext(DiagramEditingContext);
  if (!context) throw new Error('Diagram editing provider is missing.');
  return context;
}
